import _ from 'lodash'
import {
	BaseDataItem,
	pointers,
	UnresolvedEffectsList,
	UnresolvedRefArray,
	UnresolvedTimeAnimationOptions,
	UnresolvedEffects,
	NamedEffect,
	UnresolvedScrubAnimationOptions,
	UnresolvedVariantRelation,
	UnresolvedTimeAnimation,
	UnresolvedCanvasAnimationOptions,
} from '@wix/thunderbolt-becky-types'
import {
	PRESET_RESOLVING_DATA_ITEM_TYPE,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
	EFFECTS_INFLATED_MAP,
	QUERY_MAPPING,
} from '../../../constants'
import { resolveOverrideItem } from '../../dataConsumption'
import { getInflatedDataItemsMap } from '../../presetsResolving'
import type { DataRefsForPresetResolving } from '../../types'
import type { DependenciesData } from './types'
import { createConsumptionDataNode, getDataRefsForPresetResolving } from './utils/dataNodeUtils'
import { InflationDataItem } from '@wix/thunderbolt-becky-root'
import { getMultipleRefCompsIdFromInflatedId, getUniqueId } from '@wix/thunderbolt-commons'
import { removeHash, Unref } from '@wix/thunderbolt-catharsis'
import { DataItemWithDependencies } from '../../types'
import type { Ref } from '@wix/thunderbolt-types'

type Dependencies = {
	effectsListDataRefs?: Ref<DataItemWithDependencies>
	effectsDataRefs: Record<string, Ref<DataItemWithDependencies>>
	effectsDataRefsForPresetResolving: Record<string, DataRefsForPresetResolving>
}

export const Effects = createConsumptionDataNode<
	DependenciesData,
	Dependencies,
	DataRefsForPresetResolving | DataItemWithDependencies
>({
	getDependencies: (data) => {
		const { rawModel, queryName, widgetNamespace, templateComponent, createRefInTemplateNamespace } = data
		const dataRefs = { effectsDataRefs: {}, effectsDataRefsForPresetResolving: {} } as Dependencies
		const effectsMap = _.get(rawModel, [widgetNamespace, 'effects'])
		const query = templateComponent[queryName]

		if (query && effectsMap[query]) {
			return _.reduce(
				effectsMap[query].values,
				(acc, effectId) => {
					const effect = effectsMap[removeHash(effectId)] as UnresolvedEffects
					acc.effectsDataRefs[effect.id] = createRefInTemplateNamespace(QUERY_MAPPING[queryName], effect.id)

					const refArrayId = removeHash(effect.value)
					acc.effectsDataRefsForPresetResolving[refArrayId] = getDataRefsForPresetResolving(data, refArrayId)

					return acc
				},
				{
					...dataRefs,
					effectsListDataRefs: createRefInTemplateNamespace(QUERY_MAPPING[queryName], query),
				}
			)
		}

		return dataRefs
	},
	updateDependencies: ({ update, inflatedComponent, dependencies }) => {
		const { effectsListDataRefs, effectsDataRefs, effectsDataRefsForPresetResolving } = dependencies
		const ancestorsChain = getMultipleRefCompsIdFromInflatedId(inflatedComponent.id)
		if (effectsListDataRefs) {
			update(
				RESOLVED_REFS_NAMESPACE,
				'effects',
				inflatedComponent.effectsQuery,
				effectsListDataRefs,
				effectsListDataRefs
			)
		}

		for (const effectId in effectsDataRefs) {
			update(
				RESOLVED_REFS_NAMESPACE,
				'effects',
				`${getUniqueId(ancestorsChain, removeHash(effectId))}`,
				effectsDataRefs[effectId],
				effectsDataRefs[effectId]
			)
		}

		for (const refArrayId in effectsDataRefsForPresetResolving) {
			update(
				RESOLVED_REFS_NAMESPACE,
				EFFECTS_INFLATED_MAP,
				`${getUniqueId(ancestorsChain, removeHash(refArrayId))}`,
				effectsDataRefsForPresetResolving[refArrayId],
				effectsDataRefsForPresetResolving[refArrayId]
			)
		}
	},
	toViewItem: ({ id, value, rawModel, aspect }) => {
		const ancestorsChain = getMultipleRefCompsIdFromInflatedId(id)

		if (aspect === EFFECTS_INFLATED_MAP) {
			const { refArraysWithOverrides } = value as Unref<DataRefsForPresetResolving>
			const templateDataItem = _.last(refArraysWithOverrides)
			if (!templateDataItem) {
				return
			}

			if (templateDataItem.type !== 'RefArray') {
				return { [id]: resolveOverrideItem(refArraysWithOverrides, id) }
			}

			const pageVariants = _.get(rawModel, [SITE_NAMESPACE, pointers.data.DATA_MAPS.VARIANTS], {})
			const presetResolvedData = getInflatedDataItemsMap(
				value as Unref<DataRefsForPresetResolving>,
				id,
				pageVariants,
				PRESET_RESOLVING_DATA_ITEM_TYPE[pointers.data.DATA_MAPS.EFFECTS]
			)
			const unresolvedInnerEffects: Record<
				string,
				| UnresolvedRefArray
				| InflationDataItem
				| UnresolvedTimeAnimationOptions
				| UnresolvedScrubAnimationOptions
				| UnresolvedCanvasAnimationOptions
				| NamedEffect
				| UnresolvedVariantRelation
			> = {}

			_.forEach(presetResolvedData, (dataItem, dataItemId) => {
				if (
					dataItem.type === 'TimeAnimationOptions' ||
					dataItem.type === 'ScrubAnimationOptions' ||
					dataItem.type === 'CanvasAnimationOptions'
				) {
					if (dataItem.namedEffect?.id) {
						const namedEffectId = getUniqueId(ancestorsChain, removeHash(dataItem.namedEffect.id))

						unresolvedInnerEffects[namedEffectId] = { ...dataItem.namedEffect, id: namedEffectId }

						unresolvedInnerEffects[dataItem.id] = {
							...dataItem,
							namedEffect: `#${namedEffectId}`,
						} as UnresolvedTimeAnimationOptions
					} else {
						unresolvedInnerEffects[dataItem.id] = {
							...dataItem,
						} as UnresolvedTimeAnimationOptions
					}
				} else {
					unresolvedInnerEffects[dataItemId] = dataItem
				}
			})
			return unresolvedInnerEffects
		} else {
			if (!value) {
				return
			}

			const type = (value as BaseDataItem).type

			if (type === 'EffectsList') {
				const values = (value as UnresolvedEffectsList).values
				const inflatedEffects = _.map(
					values,
					(effectId) => `#${getUniqueId(ancestorsChain, removeHash(effectId))}`
				)

				return {
					...value,
					id,
					values: inflatedEffects,
				}
			} else {
				const animationOptions = (value as UnresolvedTimeAnimation).value

				return {
					...value,
					id,
					value: `#${getUniqueId(ancestorsChain, removeHash(animationOptions))}`,
				}
			}
		}
	},
})
